import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
  ViewChild,
  ElementRef,
  HostListener,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DatePickerModule } from 'primeng/datepicker';
import { HttpClient } from '@angular/common/http';
import { Subject, takeUntil, finalize } from 'rxjs';
import {
  ContractService,
  InboxRecordsPayload,
} from '../service/contract.service';

// ── Types ──────────────────────────────────────────────────────────────────

// export interface InboxRow {
//   id: number;
//   category_type: string;
//   company_id: number;
//   company_name: string;
//   contract_name: string;
//   effective_date: string; // ← new
//   expiry_date: string;
//   termination_date: string; // ← new
//   renewal_date: string;
//   termination_summary: string; // ← new
//   is_auto_renewal: string;
//   auto_renewal_summary: string; // ← new
//   comments: string; // ← new
//   status: string; // contract_status
//   // remove: subject, recipients, sender, sent_at, bcc, cc, client_id, error
// }

export interface InboxRow {
  id: number;
  category_type: string;
  company_id: number;
  company_name: string;
  contract_name: string;
  expiry_date: string;
  renewal_date: string;
  is_auto_renewal: string;
  status: string;
  // mail fields (available in list too)
  subject: string;
  sender: string;
  recipients: string[];
  cc: string[];
  bcc: string[];
  sent_at: string;
  error: string;
  client_id: number;
}

// export interface MailDetail {
//   id: number;
//   sender: string;
//   recipients: string[];
//   cc: string[];
//   bcc: string[];
//   subject: string;
//   status: string;
//   client_id: number;
//   sent_at: string;
//   error: string;
//   document: string;   // HTML or plain-text email body
//   category: string;
//   company: string;
// }

// FIXED
export interface MailDetail {
  id: number;
  sender: string;
  recipients: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  status: string;
  client_id: number;
  sent_at: string;
  error: string;
  message: string; // ← was: document
  category_type: string; // ← was: category
  company_name: string; // ← was: company
  contract_name: string;
  expiry_date: string;
  renewal_date: string;
  is_auto_renewal: string;
}

interface InboxApiResponse {
  message: string;
  result: {
    data: InboxRow[];
    pagination: {
      page: number;
      per_page: number;
      total: number;
      total_pages: number;
    };
  };
}

interface MailDetailApiResponse {
  message: string;
  result: MailDetail;
}

type SortDir = 'asc' | 'desc' | '';

// ── Component ─────────────────────────────────────────────────────────────

@Component({
  selector: 'app-contract-inbox',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePickerModule],
  providers: [DatePipe],
  templateUrl: './inbox-contracts.component.html',
  styleUrls: ['./inbox-contracts.component.scss'],
})
export class ContractInboxComponent implements OnInit, OnDestroy {
  private http = inject(HttpClient);
  private sanitizer = inject(DomSanitizer);
  private datePipe = inject(DatePipe);
  private destroy$ = new Subject<void>();

  private contractService = inject(ContractService);

  // ── API config ──────────────────────────────────────────────────────────
  private readonly CLIENT_ID = 11; // replace / inject as needed
  private readonly API_BASE = '/api/contract';

  // ── Template refs ───────────────────────────────────────────────────────
  @ViewChild('companyDropdownHost')
  companyDropdownHost!: ElementRef<HTMLElement>;

  // ── Raw data ────────────────────────────────────────────────────────────
  rows = signal<InboxRow[]>([]);
  apiTotal = signal(0);
  loading = signal(false);
  error = signal('');

  // ── Pagination ──────────────────────────────────────────────────────────
  currentPage = signal(1);
  totalPages = signal(1);
  pageSizeValue = 25;

  // ── Sorting ─────────────────────────────────────────────────────────────
  public sortCol = signal<keyof InboxRow | ''>('');
  public sortDir = signal<SortDir>('');

  // ── Pending filter state (UI controls) ──────────────────────────────────
  pendingCompanies: string[] = [];
  pendingStatus = '';
  pendingCategoryType = '';
  pendingDatePreset = 'all';
  pendingDateFromObj: Date | null = null;
  pendingDateToObj: Date | null = null;
  companyDropdownOpen = false;
  companySearchText = signal('');

  // ── Applied filter state (sent to API) ──────────────────────────────────
  appliedCompanies: string[] = [];
  appliedStatus = '';
  appliedCategoryType = '';
  appliedDatePreset = '';
  appliedDateFrom = '';
  appliedDateTo = '';

  // ── Derived options ─────────────────────────────────────────────────────
  private allCompanyOptions = signal<string[]>([]);

  filteredCompanyOptions = computed(() => {
    const q = this.companySearchText().toLowerCase();
    return q
      ? this.allCompanyOptions().filter((c) => c.toLowerCase().includes(q))
      : this.allCompanyOptions();
  });

  categoryTypes = computed(() => {
    const set = new Set(
      this.rows()
        .map((r) => r.category_type)
        .filter(Boolean),
    );
    return Array.from(set).sort();
  });

  // ── Sorted rows (client-side sort of current page) ──────────────────────
  // sortedRows = computed(() => {
  //   const col = this.sortCol();
  //   const dir = this.sortDir();
  //   if (!col || !dir) return this.rows();

  //   return [...this.rows()].sort((a, b) => {
  //     const av = (a as Record<string, unknown>)[col] ?? '';
  //     const bv = (b as Record<string, unknown>)[col] ?? '';
  //     const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
  //     return dir === 'asc' ? cmp : -cmp;
  //   });
  // });

  sortedRows = computed(() => {
    const col = this.sortCol();
    const dir = this.sortDir();

    if (!col || !dir) return this.rows();

    return [...this.rows()].sort((a, b) => {
      const av = a[col] ?? '';
      const bv = b[col] ?? '';

      const cmp = String(av).localeCompare(String(bv), undefined, {
        numeric: true,
      });

      return dir === 'asc' ? cmp : -cmp;
    });
  });

  // ── Pagination helper ───────────────────────────────────────────────────
  visiblePages = computed(() => {
    const total = this.totalPages();
    const current = this.currentPage();
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const pages: number[] = [1];
    if (current > 3) pages.push(-1);
    for (
      let p = Math.max(2, current - 1);
      p <= Math.min(total - 1, current + 1);
      p++
    ) {
      pages.push(p);
    }
    if (current < total - 2) pages.push(-1);
    pages.push(total);
    return pages;
  });

  // ── Filter chips ────────────────────────────────────────────────────────
  hasAppliedFilters = computed(
    () =>
      this.appliedCompanies.length > 0 ||
      !!this.appliedStatus ||
      !!this.appliedCategoryType ||
      !!this.appliedDatePreset ||
      !!this.appliedDateFrom ||
      !!this.appliedDateTo,
  );

  sentDateChipLabel(): string {
    if (this.appliedDatePreset === '7') return 'Sent: Last 7 Days';
    if (this.appliedDatePreset === '30') return 'Sent: Last 30 Days';
    if (this.appliedDatePreset === '60') return 'Sent: Last 60 Days';
    const from = this.appliedDateFrom;
    const to = this.appliedDateTo;
    if (from && to) return `Sent: ${from} – ${to}`;
    if (from) return `Sent from ${from}`;
    if (to) return `Sent to ${to}`;
    return 'Sent Date';
  }

  // ── Mail Preview ────────────────────────────────────────────────────────
  previewRow: InboxRow | null = null;
  mailDetail = signal<MailDetail | null>(null);
  mailDetailLoading = signal(false);
  mailDetailError = signal('');
  modalFullscreen = false;

  sanitizedBody = computed((): SafeHtml => {
    const html = (this.mailDetail() as any)?.message ?? '';
    return this.sanitizer.bypassSecurityTrustHtml(html);
  });

  // ── Lifecycle ───────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.fetchInbox();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── API calls ───────────────────────────────────────────────────────────

  private fetchInbox(): void {
    this.loading.set(true);
    this.error.set('');

    const payload: InboxRecordsPayload = {
      page: this.currentPage(),
      per_page: this.pageSizeValue,
      client_id: this.CLIENT_ID,
    };

    if (this.appliedCompanies.length)
      payload['companies'] = this.appliedCompanies;
    if (this.appliedStatus) payload['status'] = this.appliedStatus;
    if (this.appliedCategoryType)
      payload['category_type'] = this.appliedCategoryType;
    if (this.appliedDatePreset) payload['date_preset'] = this.appliedDatePreset;
    if (this.appliedDateFrom) payload['date_from'] = this.appliedDateFrom;
    if (this.appliedDateTo) payload['date_to'] = this.appliedDateTo;

    // this.http
    //   .post<InboxApiResponse>(`${this.API_BASE}/inbox`, payload)
    this.contractService
      .getInboxTotalMessage(payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loading.set(false)),
      )
      .subscribe({
        next: (res) => {
          const data = res.result.data ?? [];
          const pagination = res.result.pagination;
          this.rows.set(data);
          this.apiTotal.set(pagination.total);
          this.totalPages.set(pagination.total_pages);
          this.currentPage.set(pagination.page);

          // Populate company options from data if not yet loaded
          if (this.allCompanyOptions().length === 0) {
            const companies = Array.from(
              new Set(data.map((r) => r.company_name).filter(Boolean)),
            ).sort();
            this.allCompanyOptions.set(companies);
          }
        },
        error: (err) => {
          const msg =
            err?.error?.message ?? err?.message ?? 'Failed to load inbox.';
          this.error.set(msg);
        },
      });
  }

  openMailPreview(row: InboxRow): void {
    this.previewRow = row;
    this.mailDetail.set(null);
    this.mailDetailError.set('');
    this.mailDetailLoading.set(true);
    this.modalFullscreen = false;

    // this.http
    //   .get<MailDetailApiResponse>(`${this.API_BASE}/inbox/${row.id}`)
    this.contractService
      .getInboxMessage(row.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.mailDetailLoading.set(false)),
      )
      .subscribe({
        next: (res: any) => this.mailDetail.set(res.result),
        error: (err) => {
          const msg =
            err?.error?.message ??
            err?.message ??
            'Failed to load email details.';
          this.mailDetailError.set(msg);
        },
      });
  }

  closeMailPreview(): void {
    this.previewRow = null;
    this.mailDetail.set(null);
    this.mailDetailError.set('');
    this.modalFullscreen = false;
  }

  // ── Filters ─────────────────────────────────────────────────────────────

  applyFilters(): void {
    this.appliedCompanies = [...this.pendingCompanies];
    this.appliedStatus = this.pendingStatus;
    this.appliedCategoryType = this.pendingCategoryType;

    if (this.pendingDatePreset === 'all' || this.pendingDatePreset === '') {
      if (this.pendingDatePreset === 'all') {
        this.appliedDatePreset = '';
        this.appliedDateFrom = '';
        this.appliedDateTo = '';
      } else {
        // Custom range
        this.appliedDatePreset = '';
        this.appliedDateFrom = this.pendingDateFromObj
          ? this.formatDateForApi(this.pendingDateFromObj)
          : '';
        this.appliedDateTo = this.pendingDateToObj
          ? this.formatDateForApi(this.pendingDateToObj)
          : '';
      }
    } else {
      this.appliedDatePreset = this.pendingDatePreset;
      this.appliedDateFrom = '';
      this.appliedDateTo = '';
    }

    this.currentPage.set(1);
    this.fetchInbox();
    this.companyDropdownOpen = false;
  }

  resetFilters(): void {
    this.pendingCompanies = [];
    this.pendingStatus = '';
    this.pendingCategoryType = '';
    this.pendingDatePreset = 'all';
    this.pendingDateFromObj = null;
    this.pendingDateToObj = null;

    this.appliedCompanies = [];
    this.appliedStatus = '';
    this.appliedCategoryType = '';
    this.appliedDatePreset = '';
    this.appliedDateFrom = '';
    this.appliedDateTo = '';

    this.currentPage.set(1);
    this.fetchInbox();
  }

  removeChip(type: 'companies' | 'status' | 'category' | 'date'): void {
    if (type === 'companies') {
      this.pendingCompanies = [];
      this.appliedCompanies = [];
    }
    if (type === 'status') {
      this.pendingStatus = '';
      this.appliedStatus = '';
    }
    if (type === 'category') {
      this.pendingCategoryType = '';
      this.appliedCategoryType = '';
    }
    if (type === 'date') {
      this.pendingDatePreset = 'all';
      this.pendingDateFromObj = null;
      this.pendingDateToObj = null;
      this.appliedDatePreset = '';
      this.appliedDateFrom = '';
      this.appliedDateTo = '';
    }
    this.currentPage.set(1);
    this.fetchInbox();
  }

  onDatePresetChange(value: string): void {
    if (value !== '') {
      this.pendingDateFromObj = null;
      this.pendingDateToObj = null;
    }
  }

  // ── Company multiselect ─────────────────────────────────────────────────

  toggleCompanyDropdown(): void {
    this.companyDropdownOpen = !this.companyDropdownOpen;
  }

  isPendingCompanySelected(co: string): boolean {
    return this.pendingCompanies.includes(co);
  }

  togglePendingCompany(co: string): void {
    const idx = this.pendingCompanies.indexOf(co);
    if (idx === -1) this.pendingCompanies = [...this.pendingCompanies, co];
    else this.pendingCompanies = this.pendingCompanies.filter((c) => c !== co);
  }

  selectAllCompanies(): void {
    this.pendingCompanies = [...this.filteredCompanyOptions()];
  }

  clearPendingCompanies(): void {
    this.pendingCompanies = [];
  }

  onCompanySearch(event: Event): void {
    this.companySearchText.set((event.target as HTMLInputElement).value);
  }

  clearCompanySearch(): void {
    this.companySearchText.set('');
  }

  // ── Sorting ─────────────────────────────────────────────────────────────

  sort(col: keyof InboxRow): void {
    if (this.sortCol() === col) {
      const next: SortDir =
        this.sortDir() === 'asc'
          ? 'desc'
          : this.sortDir() === 'desc'
            ? ''
            : 'asc';
      this.sortDir.set(next);
      if (!next) this.sortCol.set('');
    } else {
      this.sortCol.set(col);
      this.sortDir.set('asc');
    }
  }

  // Returns CSS classes for the sort icon spans
  sortIconClass(col: keyof InboxRow): string {
    if (this.sortCol() !== col) return 'ci-sort-icon';
    if (this.sortDir() === 'asc') return 'ci-sort-icon ci-sort-icon--asc';
    if (this.sortDir() === 'desc') return 'ci-sort-icon ci-sort-icon--desc';
    return 'ci-sort-icon';
  }

  ariaSort(col: keyof InboxRow): 'ascending' | 'descending' | 'none' {
    if (this.sortCol() !== col) return 'none';
    if (this.sortDir() === 'asc') return 'ascending';
    if (this.sortDir() === 'desc') return 'descending';
    return 'none';
  }

  sortIcon(col: keyof InboxRow): string {
    if (this.sortCol() !== col) return '⇅';
    return this.sortDir() === 'asc' ? '↑' : '↓';
  }

  // ── Pagination ──────────────────────────────────────────────────────────

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages()) return;
    this.currentPage.set(page);
    this.fetchInbox();
  }

  onPageSizeChange(size: number): void {
    this.pageSizeValue = size;
    this.currentPage.set(1);
    this.fetchInbox();
  }

  getPagerBtnClass(p: number): string {
    if (p === -1) return 'ci-pager-btn ci-pager-btn--ellipsis';
    if (p === this.currentPage()) return 'ci-pager-btn ci-pager-btn--active';
    return 'ci-pager-btn';
  }

  // ── Formatting helpers ───────────────────────────────────────────────────

  /**
   * Parse "MM-DD-YYYY" or "MM-DD-YYYY HH:mm:ss" → display "MM/DD/YYYY"
   */
  formatDate(raw: string | null | undefined): string {
    if (!raw) return '—';
    // API returns "MM-DD-YYYY" or "MM-DD-YYYY HH:mm:ss"
    const datePart = raw.split(' ')[0]; // "MM-DD-YYYY"
    const [mm, dd, yyyy] = datePart.split('-');
    if (!mm || !dd || !yyyy) return raw;
    return `${mm}/${dd}/${yyyy}`;
  }

  /**
   * Parse "MM-DD-YYYY HH:mm:ss" → display "MM/DD/YYYY HH:mm"
   */
  formatDateTime(raw: string | null | undefined): string {
    if (!raw) return '—';
    const [datePart, timePart] = raw.split(' ');
    const [mm, dd, yyyy] = (datePart ?? '').split('-');
    if (!mm || !dd || !yyyy) return raw;
    const time = timePart ? timePart.slice(0, 5) : '';
    return time ? `${mm}/${dd}/${yyyy} ${time}` : `${mm}/${dd}/${yyyy}`;
  }

  truncate(text: string | null | undefined, max: number): string {
    if (!text) return '';
    return text.length > max ? text.slice(0, max) + '…' : text;
  }

  private formatDateForApi(d: Date): string {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
  }

  // ── Outside click to close dropdown ─────────────────────────────────────

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      this.companyDropdownOpen &&
      this.companyDropdownHost &&
      !this.companyDropdownHost.nativeElement.contains(event.target as Node)
    ) {
      this.companyDropdownOpen = false;
    }
  }

  // ── Keyboard accessibility ───────────────────────────────────────────────

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.previewRow) {
      this.closeMailPreview();
      return;
    }
    if (this.companyDropdownOpen) {
      this.companyDropdownOpen = false;
    }
  }
}
